// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { ReflectionQuestionResponseMaxCharacters, StringQuestionResponseMaxCharacters } from '../constants';
import * as models from '../generated/models';
import { isNonEmptyString, isPositiveNumber } from '../utilities';
import { glossaryTermDefinition, glossaryTermName, promptText, reason, reporterName } from './constraints';
import { cascade, maxLength, optional, range, recentTime, required, requiredBoolean } from './helpers';
import * as props from './properties';

// Validates a StandardGroup resource.
export const standardGroup = (value: Partial<models.StandardGroup>) =>
    required(value, 'group') ??
    required(value.id, 'group.id') ??
    required(value.displayName, 'group.displayName') ??
    required(value.title, 'group.title') ??
    props.uniqueIdPart(value.id, 'group.id') ??
    props.displayName(value.displayName, 'group.displayName') ??
    props.title(value.title, 'group.title') ??
    props.description(value.description, 'group.description') ??
    props.url(value.license, 'group.license') ??
    optional(value.author, (o) => contact(o, 'group.author'));

// Validates a Standard resource.
export const standard = (value: Partial<models.Standard>) =>
    required(value, 'standard') ??
    required(value.id, 'standard.id') ??
    required(value.displayName, 'standard.displayName') ??
    required(value.title, 'standard.title') ??
    props.uniqueId(value.id, 'standard.id') ??
    props.displayName(value.displayName, 'standard.displayName') ??
    props.title(value.title, 'standard.title') ??
    props.description(value.description, 'standard.description') ??
    maxLength(value.audience, 100, 'standard.audience');

// Validates a LearningPath resource.
export const learningPath = (value: Partial<models.LearningPath>) =>
    required(value, 'learningPath') ??
    required(value.id, 'learningPath.id') ??
    required(value.locale, 'learningPath.locale') ??
    required(value.title, 'learningPath.title') ??
    required(value.brand, 'learningPath.brand') ??
    props.uniqueIdPart(value.id, 'learningPath.id') ??
    props.locale(value.locale, 'learningPath.locale') ??
    props.url(value.icon, 'learningPath.icon') ??
    props.title(value.title, 'learningPath.title') ??
    props.description(value.description, 'learningPath.description') ??
    props.description(value.objectives, 'learningPath.objectives') ??
    props.description(value.brand, 'learningPath.brand') ??
    props.difficultyLevel(value.level, 'learningPath.level') ??
    optional(value.prerequisites, (o) => prerequisites(o, 'learningPath.prerequisites')) ??
    cascade(value.courses, (o) => props.uniqueIdPart(o, 'learningPath.courses'));

// Validates a Course resource.
export const course = (value: Partial<models.Course>) =>
    required(value, 'course') ??
    required(value.id, 'course.id') ??
    required(value.type, 'course.type') ??
    required(value.platform, 'course.platform') ??
    required(value.locale, 'course.locale') ??
    required(value.title, 'course.title') ??
    required(value.authors, 'course.authors') ??
    props.uniqueIdPart(value.id, 'course.id') ??
    props.courseType(value.type, 'course.type') ??
    props.coursePlatform(value.platform, 'course.platform') ??
    props.locale(value.locale, 'course.locale') ??
    props.url(value.cardImage, 'course.cardImage') ??
    props.title(value.title, 'course.title') ??
    props.description(value.description, 'course.description') ??
    props.description(value.objectives, 'course.objectives') ??
    props.difficultyLevel(value.level, 'course.level') ??
    props.duration(value.duration, 'course.duration') ??
    props.profileName(value.profileName, 'course.profileName') ??
    props.uniqueIdPart(value.pythonVersion, 'course.pythonVersion') ??
    optional(value.prerequisites, (o) => prerequisites(o, 'course.prerequisites')) ??
    optional(value.glossary, (o) => props.url(o, 'course.glossary')) ??
    cascade(value.authors, (o) => contact(o, 'course.authors')) ??
    optional(value.educatorResources, (o) => cascade(o, (o) => educatorResource(o, 'course.educatorResources'))) ??
    props.tags(value.tags, 'course.tags');

// Validates a Unit resource.
export const unit = (value: Partial<models.Unit>) =>
    required(value, 'unit') ??
    required(value.id, 'unit.id') ??
    required(value.locale, 'unit.locale') ??
    required(value.title, 'unit.title') ??
    required(value.order, 'unit.order') ??
    props.uniqueId(value.id, 'unit.id') ??
    props.locale(value.locale, 'unit.locale') ??
    props.url(value.icon, 'unit.icon') ??
    props.title(value.title, 'unit.title') ??
    props.description(value.description, 'unit.description') ??
    props.description(value.objectives, 'unit.objectives') ??
    props.difficultyLevel(value.level, 'unit.level') ??
    props.duration(value.duration, 'unit.duration') ??
    props.order(value.order, 'unit.order') ??
    optional(value.author, (o) => contact(o, 'unit.author')) ??
    optional(value.prerequisites, (o) => prerequisites(o, 'unit.prerequisites')) ??
    cascade(value.lessons, lesson);

// Validates a Lesson object.
export const lesson = (value: Partial<models.Lesson>) =>
    required(value, 'lesson') ??
    required(value.id, 'lesson.id') ??
    required(value.locale, 'lesson.locale') ??
    required(value.title, 'lesson.title') ??
    required(value.order, 'lesson.order') ??
    props.uniqueId(value.id, 'lesson.id') ??
    props.locale(value.locale, 'lesson.locale') ??
    props.url(value.icon, 'lesson.icon') ??
    props.title(value.title, 'lesson.title') ??
    props.description(value.description, 'lesson.description') ??
    props.description(value.objectives, 'lesson.objectives') ??
    props.difficultyLevel(value.level, 'lesson.level') ??
    props.duration(value.duration, 'lesson.duration') ??
    props.order(value.order, 'lesson.order') ??
    optional(value.author, (o) => contact(o, 'lesson.author')) ??
    optional(value.prerequisites, (o) => prerequisites(o, 'lesson.prerequisites')) ??
    optional(value.terminal, (o) => terminalSettings(o, 'lesson.terminal')) ??
    optional(value.layout, (o) => layoutSettings(o, 'lesson.layout')) ??
    cascade(value.editors, (o) => editorSettings(o, 'lesson.editors')) ??
    cascade(value.standards, (o) => props.uniqueId(o, 'lesson.standards')) ??
    optional(value.educatorResources, (o) => cascade(o, (o) => educatorResource(o, 'lesson.educatorResources'))) ??
    cascade(value.parts, lessonPart);

// Validates a LessonPart object.
export const lessonPart = (value: Partial<models.LessonPart>) =>
    required(value, 'lessonPart') ??
    required(value.id, 'lessonPart.id') ??
    required(value.locale, 'lessonPart.locale') ??
    required(value.title, 'lessonPart.title') ??
    required(value.order, 'lessonPart.order') ??
    props.uniqueId(value.id, 'lessonPart.id') ??
    props.locale(value.locale, 'lessonPart.locale') ??
    props.title(value.title, 'lessonPart.title') ??
    props.difficultyLevel(value.level, 'lessonPart.level') ??
    props.order(value.order, 'lessonPart.order') ??
    props.url(value.contentUri, 'lessonPart.contentUri') ??
    optional(value.prerequisites, (o) => prerequisites(o, 'lessonPart.prerequisites')) ??
    optional(value.terminal, (o) => terminalSettings(o, 'lessonPart.terminal')) ??
    optional(value.layout, (o) => layoutSettings(o, 'lessonPart.layout')) ??
    cascade(value.editors, (o) => editorSettings(o, 'lessonPart.editors')) ??
    optional(value.quiz, quiz);

// Validates a quiz object.
export const quiz = (value: Partial<models.Quiz>) =>
    required(value, 'quiz') ??
    required(value.title, 'quiz.title') ??
    required(value.title, 'quiz.questions') ??
    props.title(value.title, 'quiz.title') ??
    props.url(value.validation, 'quiz.validation') ??
    (!isPositiveNumber(value.questions?.length) && !isNonEmptyString(value.validation)
        ? 'quiz.questions must contain at least one question or validation URI.'
        : undefined) ??
    cascade(value.questions, question);

// Validates a Question object.
export const question = (value: Partial<models.Question>) =>
    required(value, 'question') ??
    required(value.id, 'question.id') ??
    required(value.content, 'question.content') ??
    required(value.type, 'question.type') ??
    required(value.responses, 'question.responses') ??
    props.uniqueId(value.id, 'question.id') ??
    props.description(value.content, 'question.content') ??
    props.description(value.hint, 'question.hint') ??
    props.questionType(value.type, 'question.type') ??
    cascade(value.choices, (o) => props.title(o, 'question.choices')) ??
    cascade(value.responses, (o) => questionResponse(o, value.type)) ??
    (isPositiveNumber(value.choices?.length) && value.type !== 'mapping'
        ? "question.choices can only be provided for questions of type 'mapping'."
        : undefined) ??
    (!isPositiveNumber(value.choices?.length) && value.type === 'mapping'
        ? "question.choices must be provided for questions of type 'mapping'."
        : undefined) ??
    (value.scrambleChoices !== undefined && value.type !== 'mapping'
        ? "question.scrambleChoices can only be set for questions of type 'mapping'."
        : undefined) ??
    (value.scrambleOrder !== undefined && value.type === 'reflection'
        ? "question.scrambleOrder cannot be set for questions of type 'reflection'."
        : undefined) ??
    (value.scrambleOrder !== undefined && value.type === 'string'
        ? "question.scrambleOrder cannot be set for questions of type 'string'."
        : undefined);

// Validates a QuestionResponse object.
export const questionResponse = (value: Partial<models.QuestionResponse>, type?: models.QuestionType) =>
    required(value, 'questionResponse') ??
    props.description(value.content, 'questionResponse.content') ??
    props.description(value.explanation, 'questionResponse.explanation') ??
    props.description(value.incorrectExplanation, 'questionResponse.incorrectExplanation') ??
    (type !== 'string' ? required(value.content, 'questionResponse.content') : undefined) ??
    (type === 'string' && typeof value.correct !== 'string'
        ? `questionResponse.correct must be a string for question of type ${type}.`
        : maxLength(value.correct as string, StringQuestionResponseMaxCharacters, 'questionResponse.correct')) ??
    (type === 'mapping' && typeof value.correct !== 'number'
        ? `questionResponse.correct must be a number for question of type ${type}.`
        : range(value.correct, 0, 100, 'questionResponse.correct')) ??
    (type === 'reflection' && value.correct !== undefined
        ? `questionResponse.correct cannot be set for question of type ${type}.`
        : undefined) ??
    ((type === 'choice' || type === 'multiple') && value.correct !== undefined && typeof value.correct !== 'boolean'
        ? `questionResponse.correct must be true or false for question of type ${type}.`
        : undefined);

// Validates a Contact object.
export const contact = (value: Partial<models.Contact>, name: string) =>
    required(value, name) ??
    required(value.displayName, `${name}.displayName`) ??
    props.displayName(value.displayName, `${name}.displayName`) ??
    props.email(value.email, `${name}.email`) ??
    props.url(value.homePage, `${name}.homePage`) ??
    props.title(value.affiliation, `${name}.affiliation`) ??
    props.url(value.photo, `${name}.photo`);

// Validates a Prerequisites object.
export const prerequisites = (value: Partial<models.Prerequisites>, name: string) =>
    required(value, name) ??
    props.description(value.description, `${name}.description`) ??
    cascade(value.recommended, (o) => props.uniqueId(o, `${name}.recommended`)) ??
    cascade(value.hiddenBefore, (o) => props.uniqueId(o, `${name}.hiddenBefore`)) ??
    cascade(value.lockedBefore, (o) => props.uniqueId(o, `${name}.lockedBefore`));

// Validates an EditorSettings object.
export const editorSettings = (value: Partial<models.EditorSettings>, name: string) =>
    required(value, name) ??
    required(value.type, `${name}.type`) ??
    props.editorType(value.type, `${name}.type`) ??
    props.url(value.template, `${name}.template`) ??
    props.url(value.filename, `${name}.filename`);

export const educatorResource = (value: models.EducatorResource, name: string) =>
    required(value, name) ??
    required(value.type, `${name}.type`) ??
    props.mimeType(value.type, `${name}.type`) ??
    required(value.title, `${name}.title`) ??
    props.title(value.title, `${name}.title`) ??
    required(value.url, `${name}.url`) ??
    props.url(value.url, `${name}.url`);

// Validates a TerminalSettings object.
export const terminalSettings = (value: Partial<models.TerminalSettings>, name: string) =>
    required(value, name) ?? required(value.type, `${name}.type`) ?? props.terminalType(value.type, `${name}.type`);

// Validates a LayoutSettings object.
export const layoutSettings = (value: Partial<models.LayoutSettings>, name: string) =>
    optional(value.components, (o) => maxLength(o, 100, `${name}.components`));

// Validates a UserProgress resource.
export const userProgress = (value: Partial<models.UserProgress>) =>
    required(value, 'userProgress') ??
    props.uniqueIdPart(value.userId, 'userProgress.userId') ??
    props.uniqueIdPart(value.courseId, 'userProgress.courseId') ??
    cascade(value.units, unitProgress);

// Validates a UnitProgress object.
export const unitProgress = (value: Partial<models.UnitProgress>) =>
    required(value, 'unitProgress') ??
    required(value.unitId, 'unitProgress.unitId') ??
    props.uniqueId(value.unitId, 'unitProgress.unitId') ??
    cascade(value.lessons, lessonProgress);

// Validates a LessonProgress object.
export const lessonProgress = (value: Partial<models.LessonProgress>) =>
    required(value, 'lessonProgress') ??
    required(value.lessonId, 'lessonProgress.lessonId') ??
    props.uniqueId(value.lessonId, 'lessonProgress.lessonId') ??
    cascade(value.parts, lessonPartProgress);

// Validates a LessonPartProgress object.
export const lessonPartProgress = (value: Partial<models.LessonPartProgress>) =>
    required(value, 'lessonPartProgress') ??
    required(value.partId, 'lessonPartProgress.partId') ??
    required(value.percentComplete, 'lessonPartProgress.percentComplete') ??
    props.uniqueId(value.partId, 'lessonPartProgress.partId') ??
    props.percentage(value.percentComplete, 'lessonPartProgress.percentComplete') ??
    cascade(value.questionAnswers, questionAnswer);

// Validates a QuestionAnswer object.
export const questionAnswer = (value: Partial<models.QuestionAnswer>) =>
    required(value, 'questionAnswer') ??
    required(value.questionId, 'questionAnswer.questionId') ??
    required(value.timestamp, 'questionAnswer.timestamp') ??
    required(value.response, 'questionAnswer.response') ??
    props.uniqueId(value.questionId, 'questionAnswer.questionId') ??
    recentTime(value.timestamp, 'questionAnswer.timestamp') ??
    (typeof value.response === 'number' ? range(value.response, 0, 100, 'questionAnswer.response') : undefined) ??
    (typeof value.response === 'string'
        ? maxLength(value.response, ReflectionQuestionResponseMaxCharacters, 'questionAnswer.response')
        : undefined) ??
    (Array.isArray(value.response)
        ? cascade(value.response, (o) =>
              typeof o === 'number'
                  ? range(o, 0, 100, 'questionAnswer.response')
                  : typeof o === 'string'
                    ? maxLength(o, StringQuestionResponseMaxCharacters, 'questionAnswer.response')
                    : 'questionAnswer.response array elements must be numbers or strings.'
          )
        : undefined) ??
    (typeof value.response !== 'number' && typeof value.response !== 'string' && !Array.isArray(value.response)
        ? 'questionAnswer.response must be a number, a string, or array of strings or numbers'
        : undefined);

// Validates a Project object.
export const project = (value: Partial<models.Project>) =>
    required(value, 'project') ??
    required(value.id, 'project.id') ??
    required(value.projectId, 'project.projectId') ??
    required(value.type, 'project.type') ??
    required(value.title, 'project.title') ??
    props.uniqueId(value.id, 'project.id') ??
    props.uniqueIdPart(value.projectId, 'project.projectId') ??
    props.editorType(value.type, 'project.type') ??
    props.title(value.title, 'project.title') ??
    props.description(value.description, 'project.description') ??
    props.url(value.publicUrl, 'project.publicUrl') ??
    recentTime(value.lastPublished, 'project.lastPublished') ??
    recentTime(value.lastUpdated, 'project.lastUpdated') ??
    props.sharedLinkId(value.sharedLinkId, 'project.sharedLinkId') ??
    props.tags(value.tags, 'curriculum.tags');

// Validates a ProjectTemplate object.
export const projectTemplate = (value: Partial<models.ProjectTemplate>) =>
    required(value, 'projectTemplate') ??
    required(value.id, 'projectTemplate.id') ??
    required(value.type, 'projectTemplate.type') ??
    required(value.title, 'projectTemplate.title') ??
    required(value.description, 'projectTemplate.description') ??
    required(value.icon, 'projectTemplate.icon') ??
    props.uniqueIdPart(value.id, 'projectTemplate.id') ??
    props.editorType(value.type, 'projectTemplate.type') ??
    props.title(value.title, 'projectTemplate.title') ??
    props.description(value.description, 'projectTemplate.description') ??
    props.url(value.icon, 'projectTemplate.icon') ??
    props.tags(value.tags, 'projectTemplate.tags');

// Validates user session options.
export const startSessionOptions = (value: Partial<models.StartSessionOptions>) =>
    required(value, 'startSessionOptions') ??
    required(value.authProvider, 'startSessionOptions.authProvider') ??
    required(value.redirectUrl, 'startSessionOptions.redirectUrl') ??
    props.absoluteUrl(value.redirectUrl, 'startSessionOptions.redirectUrl') ??
    requiredBoolean(value.longTerm, 'startSessionOptions.longTermSession');

// Validates user session state.
export const state = (value: string | undefined) => required(value, 'state') ?? props.jwt(value, 'state');

// Validate authentication verifier code value.
export const codeVerifier = (value: string | undefined) =>
    required(value, 'codeVerifier') ?? props.uuid(value, 'codeVerifier');

// Validate authentication OTAC code value.
export const otac = (value: string | undefined) => required(value, 'otac') ?? props.otacType(value, 'otac');

// Validate auth provider
export const authProvider = (value: string | undefined) =>
    required(value, 'authProvider') ?? props.authProviderType(value, 'authProvider');

// Validates glossary and glossary term resources.
export const glossary = (value: Partial<models.Glossary>) =>
    required(value, 'glossary') ??
    required(value.id, 'glossary.id') ??
    props.uniqueIdPart(value.id, 'glossary.id') ??
    cascade(value.terms, glossaryTerm);

// Validates a glossary term resource.
export const glossaryTerm = (value: Partial<models.GlossaryTerm>) =>
    required(value, 'glossaryTerm') ??
    required(value.term, 'glossaryTerm.term') ??
    required(value.definition, 'glossaryTerm.definition') ??
    maxLength(value.term, glossaryTermName.MaxLength ?? 100, 'glossaryTerm.term') ??
    maxLength(value.definition, glossaryTermDefinition.MaxLength ?? 4000, 'glossaryTerm.definition');

// Validates writable properties of a curriculum resource.
export const curriculum = (value: Partial<models.Curriculum>) =>
    required(value, 'curriculum') ??
    required(value.type, 'curriculum.type') ??
    required(value.platform, 'curriculum.platform') ??
    required(value.title, 'curriculum.title') ??
    props.courseType(value.type, 'curriculum.type') ??
    props.coursePlatform(value.platform, 'curriculum.platform') ??
    props.title(value.title, 'curriculum.title') ??
    props.description(value.description, 'curriculum.description') ??
    props.tags(value.tags, 'curriculum.tags');

// Validates a content report object.
export const contentReport = (value: Partial<models.UserContentReport>) =>
    required(value, 'contentReport') ??
    required(value.url, 'contentReport.url') ??
    required(value.abuseCategory, 'contentReport.abuseCategory') ??
    props.url(value.url, 'contentReport.url') ??
    props.abuseCategory(value.abuseCategory, 'contentReport.abuseCategory') ??
    maxLength(value.reason, reason.MaxLength ?? 4000, 'contentReport.reason') ??
    maxLength(value.reporterName, reporterName.MaxLength ?? 250, 'contentReport.reporterName') ??
    props.email(value.reporterEmail, 'contentReport.reporterEmail');

// Validates a prompt request object.
export const promptRequest = (value: Partial<models.PromptRequest>) =>
    required(value, 'promptRequest') ??
    required(value.text, 'promptRequest.text') ??
    maxLength(value.text, promptText.MaxLength ?? 60000, 'promptRequest.text');
